import { computed, reactive, watch, watchEffect } from 'vue';

// COMPOSABLES
import { usePinFlow } from '../../composables/usePinFlow';
import { injectPinRestore } from '../../composables/usePinRestoreProvider';

// HELPERS
import { verifyPinSecurity } from '../../utils/verifyPinSecurity';

// TYPES
import { PinRestoreStep, PinType } from '../../types/pin.type';
import type { PinEmits, UsePinRestoreCodeReturn } from '../../types/pin.type';

export function usePinRestoreCode(emit: PinEmits): UsePinRestoreCodeReturn {
  const { step, currentPin } = injectPinRestore();

  const pinFlow = usePinFlow();

  const pin = reactive({
    value: '',
    complete: false,
    error: '',
  });

  const repeat = reactive({
    value: '',
    complete: false,
    error: '',
  });

  const isDisabled = computed(() => {
    return (
      !pin.complete ||
      Boolean(pin.error) ||
      !repeat.complete ||
      Boolean(repeat.error)
    );
  });

  const isTypeChange = computed(() => pinFlow.type.value === PinType.CHANGE);

  function onCompletePin(isComplete: boolean) {
    if (isComplete) {
      pin.error = verifyPinSecurity({ pin: pin.value });
      pin.complete = isComplete;
      return;
    }
    pin.error = '';
  }

  function onChangePin(value: string) {
    pin.value = value;
  }

  function onChangePinRepeat(value: string) {
    repeat.value = value;
  }

  function onCompletePinRepeat(isComplete: boolean) {
    repeat.complete = isComplete;
  }

  watchEffect(() => {
    if (pin.complete && repeat.complete && pin.value !== repeat.value) {
      repeat.error = 'El PIN no coincide';
      return;
    }

    pinFlow.setPinError('');
    repeat.error = '';
  });

  watch(pinFlow.error, (newError) => {
    if (newError) {
      repeat.error = newError;
      pin.error = ' ';
      return;
    }

    repeat.error = '';
    pin.error = '';
  });

  async function onNext() {
    if (isTypeChange.value) {
      await pinFlow.onNext.value(pin.value);
      if (!pinFlow.error.value) {
        emit('close');
        pinFlow.hidePin();
      }
      return;
    }

    currentPin.value = pin.value;
    step.value = PinRestoreStep.VERIFICATION;
  }

  function onClose() {
    emit('close');
    pinFlow.hidePin();
  }

  function onKeyPressToValidate() {
    if (!isDisabled.value) {
      onNext();
    }
  }

  function getSubtitleText() {
    return pinFlow.description.value
      ? pinFlow.description.value
      : '¡Recuerda elegir un PIN que sea fácil de recordar!';
  }

  function getTitleText() {
    return pinFlow.title.value ? pinFlow.title.value : 'Crea tu nuevo PIN';
  }

  return {
    pin,
    repeat,
    isDisabled,
    isTypeChange,
    getTitleText,
    getSubtitleText,
    onClose,
    onKeyPressToValidate,
    onCompletePin,
    onChangePin,
    onCompletePinRepeat,
    onChangePinRepeat,
    onNext,
  };
}
